/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import getConfig from '../../config';
import { getLogger } from '../../utils/SearchLogger';
import uuid from 'uuid/v4';
import Request from '../../utils/Request';
import PerformanceUtils from '../../utils/PerformanceUtils';
import { Utils } from '../../utils/Globals';
import SearchUtils from '../../utils/SearchUtils';
import SearchHostContext from '../../utils/SearchHostContext';

const fetchAppMetadata = async ({
	locale,
	searchHostContext,
}: {
	locale: string;
	searchHostContext: HostContext;
}): Promise<unknown> => {
	try {
		const isCDNEnabledFromFloodgate = SearchUtils.isFloodgateFeatureInTest('ccs.cdn.appsdata', searchHostContext);
		const hostname = isCDNEnabledFromFloodgate
			? getConfig(searchHostContext.environment, 'SEARCH_SERVER_CDN')
			: getConfig(searchHostContext.environment, 'SEARCH_SERVER');
		const clientPlatform = new Utils(searchHostContext).getPlatform();
		const version = SearchUtils.isFloodgateFeatureInTest('ccs.apps.v3', SearchHostContext.getContext())
			? 'v3'
			: 'v2';
		const path = `/${version}/search-apps?locale=${locale}&platform=${clientPlatform}`;

		const curRequestId = uuid();
		const startTime = Date.now();
		const headers = {
			'Content-Type': 'application/json',
			'x-api-key': searchHostContext.hostInfo.apiKey,
			'x-request-id': curRequestId,
		};
		const appMetadataResponse = await Request.withHeaders(headers).get(`https://${hostname}${path}`);

		if (appMetadataResponse.isError()) {
			const loggerData = {
				url: `https://${hostname}${path}`,
				status: appMetadataResponse.status(),
				message: appMetadataResponse.statusText(),
				requestId: curRequestId,
				duration: Date.now() - startTime,
				performanceTiming: PerformanceUtils.getPerformanceTimingByName(appMetadataResponse.requestUrl()),
			};
			getLogger().error('Search Service', 'Problem with Search apps meta data', loggerData);
			const appMetadataError = new Error('Search service search apps error');
			return appMetadataError;
		}

		return appMetadataResponse.data();
	} catch (e) {
		getLogger().error('Search Service', 'Problem with Search apps meta data', e);
		const appMetadataError = new Error('Search service search apps error');
		return appMetadataError;
	}
};

export { fetchAppMetadata };
